(() => {
  const TAG = 'm-explore-videos';
  if (customElements.get(TAG)) return;
  const playIcon = '<svg viewBox="0 0 24 24" class="fv-fill"><polygon points="5 3 19 12 5 21 5 3"/></svg>';
  const pauseIcon = '<svg viewBox="0 0 24 24"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>';
  const speaker = '<polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>';
  function loadVimeo() {
    if (window.Vimeo?.Player) return Promise.resolve(window.Vimeo);
    if (window.minimogExploreVimeo) return window.minimogExploreVimeo;
    window.minimogExploreVimeo = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://player.vimeo.com/api/player.js';
      script.onload = () => window.Vimeo?.Player ? resolve(window.Vimeo) : reject(new Error('Vimeo unavailable'));
      script.onerror = () => reject(new Error('Vimeo unavailable'));
      const timeout = setTimeout(() => reject(new Error('Vimeo timed out')), 15000);
      script.addEventListener('load', () => clearTimeout(timeout), { once: true });
      script.addEventListener('error', () => clearTimeout(timeout), { once: true });
      document.head.appendChild(script);
    }).catch(error => { window.minimogExploreVimeo = null; throw error; });
    return window.minimogExploreVimeo;
  }
  class ExploreVideos extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.stage = this.querySelector('[data-stage]');
      this.cards = Array.from(this.querySelectorAll('[data-card]'));
      this.players = [];
      this.activeIdx = 0;
      this.isMuted = true;
      this.isPaused = true;
      this.playRequested = true;
      this.events = new AbortController();
      this.session = {};
      const session = this.session;
      this.querySelectorAll('[data-prev],[data-next]').forEach(button => {
        button.hidden = this.cards.length < 2;
        if (this.cards.length < 2) button.style.setProperty('display', 'none', 'important');
        else button.style.removeProperty('display');
      });
      if (!this.stage || !this.cards.length) {
        if (this.stage) this.stage.style.height = '0px';
        return;
      }
      const listen = (target, type, handler, options = {}) => target.addEventListener(type, handler, { ...options, signal: this.events.signal });
      listen(this, 'click', event => this.onClick(event), { capture: true });
      listen(this.stage, 'keydown', event => {
        if (event.target !== this.stage || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault(); this.navigate(event.key === 'ArrowLeft' ? -1 : 1);
      });
      listen(this.stage, 'touchstart', event => {
        this.touch = event.touches.length === 1 ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
      }, { passive: true });
      listen(this.stage, 'touchend', event => {
        if (!this.touch || !event.changedTouches.length) return;
        const dx = this.touch.x - event.changedTouches[0].clientX;
        const dy = this.touch.y - event.changedTouches[0].clientY;
        this.touch = null;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.25) {
          this.suppressClickUntil = Date.now() + 450;
          this.navigate(dx > 0 ? 1 : -1);
        }
      }, { passive: true });
      listen(this.stage, 'touchcancel', () => { this.touch = null; }, { passive: true });
      listen(window, 'resize', () => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => this.applyLayout(), 120);
      });
      this.applyLayout();
      this.cards.forEach((card, i) => {
        const video = card.querySelector('[data-video]');
        if (!video) return;
        this.players[i] = {
          play: () => video.play(), pause: () => { video.pause(); return Promise.resolve(); },
          setMuted: muted => { video.muted = muted; return Promise.resolve(); }
        };
        listen(video, 'play', () => this.onPlayback(i, false));
        listen(video, 'pause', () => this.onPlayback(i, true));
        listen(video, 'error', () => this.onPlayback(i, true));
      });
      this.syncPlayback();
      if (this.querySelector('[data-iframe]')) loadVimeo().then(Vimeo => {
        if (this.session !== session || !this.isConnected) return;
        this.cards.forEach((card, i) => {
          const iframe = card.querySelector('[data-iframe]');
          if (!iframe) return;
          const player = new Vimeo.Player(iframe);
          this.players[i] = player;
          player.on('play', () => this.onPlayback(i, false));
          player.on('pause', () => this.onPlayback(i, true));
          player.on('error', () => this.onPlayback(i, true));
        });
        this.syncPlayback();
      }).catch(() => { if (this.session === session) { this.isPaused = true; this.syncIcons(); } });
    }
    wrapOffset(index) {
      const n = this.cards.length;
      let offset = ((index - this.activeIdx) % n + n) % n;
      if (offset > n / 2) offset -= n;
      return offset;
    }
    applyLayout() {
      if (!this.cards.length) return;
      const card = this.cards[this.activeIdx], step = card.offsetWidth + (window.innerWidth < 768 ? 14 : 20);
      this.stage.style.height = card.offsetHeight + 'px';
      this.cards.forEach((card, i) => {
        const offset = this.wrapOffset(i), distance = Math.abs(offset);
        card.classList.remove('fv-center', 'fv-side1', 'fv-side2', 'fv-hidden');
        card.classList.add(distance === 0 ? 'fv-center' : distance === 1 ? 'fv-side1' : distance === 2 ? 'fv-side2' : 'fv-hidden');
        card.style.transform = `translateX(${offset * step}px) scale(${distance === 0 ? 1 : distance === 1 ? .88 : distance === 2 ? .78 : .7})`;
        card.setAttribute('aria-hidden', String(distance > 2));
        card.inert = distance > 2;
        card.querySelectorAll('[data-pp],[data-mute]').forEach(button => { button.tabIndex = distance === 0 ? 0 : -1; });
      });
      this.syncIcons();
    }
    navigate(steps) {
      if (this.cards.length < 2) return;
      this.activeIdx = ((this.activeIdx + steps) % this.cards.length + this.cards.length) % this.cards.length;
      this.playRequested = true;
      this.isPaused = true;
      this.applyLayout();
      this.syncPlayback();
    }
    onClick(event) {
      if (Date.now() < (this.suppressClickUntil || 0)) { event.preventDefault(); event.stopPropagation(); return; }
      const prev = event.target.closest('[data-prev]'), next = event.target.closest('[data-next]');
      if (prev || next) { event.preventDefault(); this.navigate(prev ? -1 : 1); return; }
      const card = event.target.closest('[data-card]');
      if (!card || !this.contains(card) || card.classList.contains('fv-hidden')) return;
      if (event.target.closest('a')) return;
      const index = this.cards.indexOf(card);
      if (index !== this.activeIdx) { event.preventDefault(); this.navigate(this.wrapOffset(index)); return; }
      if (event.target.closest('[data-mute]')) {
        event.preventDefault(); this.isMuted = !this.isMuted; this.syncIcons(); this.syncPlayback();
      } else {
        event.preventDefault(); this.playRequested = this.isPaused; this.syncPlayback();
      }
    }
    onPlayback(index, paused) {
      if (index !== this.activeIdx) {
        if (!paused) Promise.resolve(this.players[index]?.pause()).catch(() => {});
        return;
      }
      this.isPaused = paused; this.syncIcons();
    }
    syncPlayback() {
      const revision = this.playRevision = (this.playRevision || 0) + 1;
      const session = this.session;
      this.players.forEach((player, index) => {
        if (!player) return;
        if (index !== this.activeIdx || !this.playRequested) {
          Promise.resolve(player.pause()).catch(() => {});
          if (index === this.activeIdx) { this.isPaused = true; this.syncIcons(); }
          return;
        }
        Promise.resolve(player.setMuted(this.isMuted)).then(() => {
          if (this.session === session && this.playRevision === revision && this.activeIdx === index && this.playRequested) return player.play();
        }).catch(() => { if (this.session === session && this.activeIdx === index && this.playRevision === revision) { this.isPaused = true; this.syncIcons(); } });
      });
    }
    syncIcons() {
      const card = this.cards[this.activeIdx];
      if (!card) return;
      const pp = card.querySelector('[data-pp]'), mute = card.querySelector('[data-mute]');
      if (pp) { pp.innerHTML = this.isPaused ? playIcon : pauseIcon; pp.setAttribute('aria-label', this.isPaused ? 'Play video' : 'Pause video'); }
      if (mute) {
        mute.innerHTML = `<svg viewBox="0 0 24 24">${speaker}${this.isMuted ? '<line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/>' : '<path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/>'}</svg>`;
        mute.setAttribute('aria-label', this.isMuted ? 'Unmute video' : 'Mute video');
      }
    }
    disconnectedCallback() {
      this.session = null;
      this.events?.abort(); this.events = null;
      clearTimeout(this.resizeTimer);
      this.touch = null;
      (this.players || []).forEach(player => {
        if (player.off) { player.off('play'); player.off('pause'); player.off('error'); }
        Promise.resolve(player.pause()).catch(() => {});
      });
      this.players = [];
    }
  }
  customElements.define(TAG, ExploreVideos);
})();
